import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronsUpDown, KeyRound, LogOut, Settings, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { ChangePasswordDialog } from "@/components/layout/ChangePasswordDialog";
import { AdvisorSheet } from "@/features/admin/AdvisorSheet";
import { logout } from "@/lib/api/auth";
import { ANONYMOUS_BOOTSTRAP, useSession } from "@/lib/session/SessionContext";
import { useMemberLogout } from "@/lib/session/useMemberLogout";
import { getInitials } from "@/lib/utils";

/**
 * Der Fuß der linken Leiste: wer angemeldet ist, und was man mit dem eigenen
 * Konto tun kann. Übernimmt das Kontomenü aus AuthenticatedHeader.tsx für
 * Bearbeiter und den Abmelde-Weg aus MemberPageShell.tsx für Mitglieder —
 * beide Rollen sitzen jetzt im selben Rahmen, das Menü dahinter bleibt
 * verschieden:
 *
 *  - **Bearbeiter** haben ein Profil und Einstellungen (`advisors` trägt die
 *    frische Fassung nach einem Selbst-Edit, `session.user` nur den
 *    Login-Abzug).
 *  - **Mitglieder** haben weder das eine noch das andere; sie können ihr
 *    Passwort ändern. Ihr Abmelden verwirft zusätzlich den Offline-Tagesbestand
 *    (useMemberLogout.ts), deshalb zwei Wege statt einem.
 */
export function SidebarAccount() {
  const navigate = useNavigate();
  const { isMobile } = useSidebar();
  const { session, clients, advisors, applyBootstrap } = useSession();
  const { handleLogout: memberLogout, pending } = useMemberLogout();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const me = session?.actorType === "admin" ? (advisors.find((advisor) => advisor.id === session.user.id) ?? session.user) : null;

  const adminLogout = async () => {
    await logout().catch(() => {});
    applyBootstrap(ANONYMOUS_BOOTSTRAP);
    navigate("/login");
  };

  let name = "";
  let unterzeile = "";
  let bildAdresse: string | undefined;
  if (me) {
    name = me.name;
    unterzeile = me.email;
    bildAdresse = me.avatar || undefined;
  } else if (session?.actorType === "member") {
    name = "Mitglied";
    unterzeile = session.role;
  } else if (session?.actorType === "customer") {
    const client = clients[0];
    name = client?.name ?? "";
    unterzeile = "Onboarding";
    bildAdresse = client?.data.documents.uploadedFiles.find((file) => file.type === "Favicon")?.downloadUrl;
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger render={<SidebarMenuButton size="lg" tooltip={name} aria-label={`Kontomenü für ${name}`} className="data-popup-open:bg-sidebar-accent" />}>
            <Avatar className="size-8 rounded-lg">
              {bildAdresse ? <AvatarImage src={bildAdresse} alt="" /> : null}
              <AvatarFallback className="rounded-lg">{getInitials(name)}</AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left leading-tight">
              <span className="truncate text-sm font-medium">{name}</span>
              <span className="truncate text-xs text-muted-foreground">{unterzeile}</span>
            </div>
            <ChevronsUpDown aria-hidden="true" className="ml-auto" />
          </DropdownMenuTrigger>
          <DropdownMenuContent side={isMobile ? "bottom" : "right"} align="end" className="min-w-56">
            <DropdownMenuGroup>
              <DropdownMenuLabel>{name}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {me ? (
                <>
                  <DropdownMenuItem onClick={() => setProfileOpen(true)}>
                    <User aria-hidden="true" /> Mein Profil
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/admin/settings")}>
                    <Settings aria-hidden="true" /> Einstellungen
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem onClick={() => setPasswordDialogOpen(true)}>
                  <KeyRound aria-hidden="true" /> Passwort ändern
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled={pending} onClick={me ? adminLogout : memberLogout}>
                <LogOut aria-hidden="true" /> Abmelden
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>

      <ChangePasswordDialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen} />
      {me ? <AdvisorSheet advisor={me} open={profileOpen} onOpenChange={setProfileOpen} /> : null}
    </SidebarMenu>
  );
}
